import { type ReactNode, useEffect, useMemo, useState } from "react";

import {
  createManagerAuthorization,
  formatFen,
  getHomeDraft,
  getOrganizerToken,
  getWorkbench,
  listOrganizerActivities,
  listManagerAuthorizations,
  saveHomeDraft,
  setOrganizerToken,
  submitHomeDraft,
  textValue,
  updateOrganizerActivity,
  uploadOrganizerHomeMedia,
  type OrganizerHomeDraft,
  type OrganizerMediaFile,
  type OrganizerRecord,
  type OrganizerWorkbenchResponse
} from "./api";

type SectionId = "workbench" | "home" | "activities" | "operations" | "members";
type MediaUploadSlot = "background" | "avatar" | "gallery";
type MediaPreviewRatio = "wide" | "avatar" | "gallery";

type ImageToolState = {
  slot: MediaUploadSlot;
  file: File;
  objectUrl: string;
  zoom: number;
  offsetX: number;
  offsetY: number;
};

const defaultOrganizationId = "org_owned";

const navItems: Array<{ id: SectionId; label: string; group: string }> = [
  { id: "workbench", label: "工作台", group: "总览" },
  { id: "home", label: "组织主页", group: "内容资产" },
  { id: "activities", label: "活动管理", group: "活动运营" },
  { id: "operations", label: "经营数据", group: "活动运营" },
  { id: "members", label: "协作成员", group: "组织协作" }
];

const maxImageSizeBytes = 10 * 1024 * 1024;
const acceptedImageTypes = ["image/jpeg", "image/png", "image/webp"];

const imageCropSpecs: Record<
  MediaUploadSlot,
  {
    title: string;
    ratioLabel: string;
    width: number;
    height: number;
    recommendation: string;
    limit: string;
    previewClass: MediaPreviewRatio;
  }
> = {
  background: {
    title: "跑团背景图",
    ratioLabel: "16:7",
    width: 1600,
    height: 700,
    recommendation: "建议 1440 x 630 像素以上，适合主页顶部横幅展示",
    limit: "JPG/PNG/WebP，单张最大 10MB",
    previewClass: "wide"
  },
  avatar: {
    title: "跑团头像",
    ratioLabel: "1:1",
    width: 1000,
    height: 1000,
    recommendation: "建议 800 x 800 像素以上，适合圆形头像展示",
    limit: "JPG/PNG/WebP，单张最大 10MB",
    previewClass: "avatar"
  },
  gallery: {
    title: "主页图集图片",
    ratioLabel: "4:3",
    width: 1200,
    height: 900,
    recommendation: "建议 1200 x 900 像素以上，适合图集列表展示",
    limit: "JPG/PNG/WebP，单张最大 10MB",
    previewClass: "gallery"
  }
};

const defaultDraft: OrganizerHomeDraft = {
  organizationId: defaultOrganizationId,
  version: 0,
  status: "DRAFT",
  organizationName: "",
  slogan: "",
  serviceCities: [],
  galleryFileIds: [],
  galleryFiles: [],
  longFormIntro: "",
  moduleOrder: ["intro", "gallery", "activities"],
  publicVisible: true
};

function splitCsv(value: string) {
  return value.split(/[,\s，]+/).map((item) => item.trim()).filter(Boolean);
}

function isAcceptedImageFile(file: File) {
  return acceptedImageTypes.includes(file.type) || /\.(jpe?g|png|webp)$/i.test(file.name);
}

function formatFileSize(bytes: number) {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
  if (bytes >= 1024) return `${Math.ceil(bytes / 1024)}KB`;
  return `${bytes}B`;
}

function imageZoomScale(value: number) {
  const normalized = Math.max(-100, Math.min(100, value));
  if (normalized < 0) return 1 + normalized / 200;
  return 1 + normalized / 50;
}

function loadBrowserImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("图片读取失败，请更换图片后重试"));
    image.src = src;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error("图片生成失败，请更换图片后重试"));
      }
    }, type, quality);
  });
}

async function createCroppedImageFile(tool: ImageToolState) {
  const spec = imageCropSpecs[tool.slot];
  const image = await loadBrowserImage(tool.objectUrl);
  const naturalWidth = image.naturalWidth || image.width;
  const naturalHeight = image.naturalHeight || image.height;
  const canvas = document.createElement("canvas");
  canvas.width = spec.width;
  canvas.height = spec.height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("当前浏览器无法处理图片");
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, spec.width, spec.height);

  const baseScale = Math.max(spec.width / naturalWidth, spec.height / naturalHeight);
  const zoom = imageZoomScale(tool.zoom);
  const drawWidth = naturalWidth * baseScale * zoom;
  const drawHeight = naturalHeight * baseScale * zoom;
  const offsetX = spec.width * (Math.max(-100, Math.min(100, tool.offsetX)) / 800);
  const offsetY = spec.height * (Math.max(-100, Math.min(100, tool.offsetY)) / 800);
  const drawX = (spec.width - drawWidth) / 2 + offsetX;
  const drawY = (spec.height - drawHeight) / 2 + offsetY;

  context.drawImage(image, drawX, drawY, drawWidth, drawHeight);
  const blob = await canvasToBlob(canvas, "image/jpeg", 0.9);
  const baseName = tool.file.name.replace(/\.[^.]+$/, "") || tool.slot;
  return new File([blob], `${baseName}-${tool.slot}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
}

export function App() {
  const [active, setActive] = useState<SectionId>("workbench");
  const [workbench, setWorkbench] = useState<OrganizerWorkbenchResponse>({});
  const [draft, setDraft] = useState<OrganizerHomeDraft>(defaultDraft);
  const [managerRows, setManagerRows] = useState<OrganizerRecord[]>([]);
  const [token, setToken] = useState(getOrganizerToken());
  const [organizationId, setOrganizationId] = useState(defaultOrganizationId);
  const [status, setStatus] = useState("正在读取组织者后台数据");
  const [saving, setSaving] = useState(false);
  const [uploadingSlot, setUploadingSlot] = useState("");

  async function load() {
    setStatus("正在读取组织者后台数据");
    try {
      const requestedOrganizationId = organizationId || defaultOrganizationId;
      const workbenchData = await getWorkbench(requestedOrganizationId);
      const resolvedOrganizationId = workbenchData.organization?.id || workbenchData.homeDraft?.organizationId || organizationId || defaultOrganizationId;
      const [homeData, managerData] = await Promise.all([getHomeDraft(resolvedOrganizationId), listManagerAuthorizations(resolvedOrganizationId)]);
      setOrganizationId(resolvedOrganizationId);
      setWorkbench(workbenchData);
      setDraft({ ...defaultDraft, ...homeData, organizationId: resolvedOrganizationId });
      setManagerRows(managerData);
      setStatus("数据已同步");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "数据读取失败");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function saveToken() {
    setOrganizerToken(token.trim());
    void load();
  }

  async function saveDraft(submitReview = false, draftOverride?: OrganizerHomeDraft) {
    const draftToSave = draftOverride || draft;
    setSaving(true);
    setStatus("正在保存主页草稿");
    try {
      const saved = await saveHomeDraft(draftToSave);
      const next = submitReview ? await submitHomeDraft(draftToSave.organizationId, saved.id) : saved;
      setDraft({ ...draftToSave, ...next });
      setStatus(submitReview ? "主页草稿已提交审核" : "主页草稿已保存");
      await load();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "主页草稿保存失败");
    } finally {
      setSaving(false);
    }
  }

  async function addManager() {
    setSaving(true);
    setStatus("正在新增管理者授权");
    try {
      const firstActivity = workbench.activities?.[0];
      await createManagerAuthorization({
        organizationId,
        activityId: String(firstActivity?.id || "act_river_run"),
        managerUserId: "u_manager_001",
        reason: "PC 后台授权现场执行"
      });
      setManagerRows(await listManagerAuthorizations(organizationId));
      setStatus("管理者授权已生效");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "授权失败");
    } finally {
      setSaving(false);
    }
  }

  const groupedNav = useMemo(() => {
    return navItems.reduce<Record<string, typeof navItems>>((groups, item) => {
      groups[item.group] = [...(groups[item.group] || []), item];
      return groups;
    }, {});
  }, []);

  return (
    <div className="organizerShell">
      <aside className="sidebar">
        <div className="brandBlock">
          <span className="brandMark">AB</span>
          <div>
            <strong>活动邦组织者后台</strong>
            <span>ab-cms.11aigc.com</span>
          </div>
        </div>
        <nav>
          {Object.entries(groupedNav).map(([group, items]) => (
            <div className="navGroup" key={group}>
              <span>{group}</span>
              {items.map((item) => (
                <button key={item.id} className={active === item.id ? "active" : ""} onClick={() => setActive(item.id)}>
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="routeNote">
          <strong>部署路径</strong>
          <span>/ 服务组织者后台</span>
          <span>/cms 服务平台 CMS</span>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <div>
            <p className="eyebrow">OFFICIAL ORGANIZER CONSOLE</p>
            <h1>{workbench.organization?.name || "组织者管理后台"}</h1>
          </div>
          <div className="tokenBox">
            <input value={token} placeholder="Organizer session token" onChange={(event) => setToken(event.target.value)} />
            <button onClick={saveToken}>保存会话</button>
          </div>
        </header>

        <div className={status.includes("失败") || status.includes("无权限") ? "statusBar error" : "statusBar"}>{status}</div>

        {active === "workbench" ? <WorkbenchPanel workbench={workbench} setActive={setActive} /> : null}
        {active === "home" ? (
          <HomePanel
            draft={draft}
            setDraft={setDraft}
            saving={saving}
            uploadingSlot={uploadingSlot}
            setUploadingSlot={setUploadingSlot}
            setStatus={setStatus}
            onSubmit={(nextDraft) => saveDraft(true, nextDraft)}
          />
        ) : null}
        {active === "activities" ? <ActivitiesPanel rows={workbench.activities || []} organizationId={workbench.organization?.id || organizationId} /> : null}
        {active === "operations" ? <OperationsPanel workbench={workbench} /> : null}
        {active === "members" ? <MembersPanel rows={managerRows} saving={saving} onAdd={addManager} /> : null}
      </main>
    </div>
  );
}

function WorkbenchPanel({ workbench, setActive }: { workbench: OrganizerWorkbenchResponse; setActive: (value: SectionId) => void }) {
  const summary = workbench.summary || {};
  return (
    <section className="sectionGrid">
      <div className="panel wide">
        <div className="panelHead">
          <div>
            <p className="eyebrow">工作台</p>
            <h2>组织经营摘要</h2>
          </div>
          <span className="tag success">{workbench.organization?.status || "CERTIFIED"}</span>
        </div>
        <div className="metricGrid">
          <Metric label="活动数" value={summary.activities || 0} />
          <Metric label="报名人数" value={summary.registrations || 0} />
          <Metric label="签到人数" value={summary.checkIns || 0} />
          <Metric label="待结算" value={formatFen(summary.settlementFen)} />
        </div>
      </div>
      <div className="panel">
        <div className="panelHead">
          <div>
            <p className="eyebrow">主页状态</p>
            <h2>草稿与审核</h2>
          </div>
          <span className="tag">{workbench.homeDraft?.status || "DRAFT"}</span>
        </div>
        <p className="bodyText">{workbench.homeDraft?.slogan || "维护组织主页资料、图集、长图文和模块排序。"}</p>
        <button onClick={() => setActive("home")}>进入主页管理</button>
      </div>
      <div className="panel">
        <div className="panelHead">
          <div>
            <p className="eyebrow">待处理</p>
            <h2>工作提醒</h2>
          </div>
        </div>
        <div className="plainList">
          {(workbench.pendingTasks?.length ? workbench.pendingTasks : [{ title: "暂无待处理事项" }]).map((item, index) => (
            <span key={index}>{textValue(item.title)}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

function HomePanel({
  draft,
  setDraft,
  saving,
  uploadingSlot,
  setUploadingSlot,
  setStatus,
  onSubmit
}: {
  draft: OrganizerHomeDraft;
  setDraft: (draft: OrganizerHomeDraft) => void;
  saving: boolean;
  uploadingSlot: string;
  setUploadingSlot: (value: string) => void;
  setStatus: (value: string) => void;
  onSubmit: (draft?: OrganizerHomeDraft) => Promise<void>;
}) {
  const [imageTool, setImageTool] = useState<ImageToolState>();

  function openImageTool(slot: MediaUploadSlot, file?: File) {
    if (!file) return;
    if (!isAcceptedImageFile(file)) {
      setStatus("仅支持 JPG、PNG、WebP 图片");
      return;
    }
    if (file.size > maxImageSizeBytes) {
      setStatus(`图片大小为 ${formatFileSize(file.size)}，已超过单张 10MB 限制`);
      return;
    }
    if (imageTool?.objectUrl) URL.revokeObjectURL(imageTool.objectUrl);
    setImageTool({
      slot,
      file,
      objectUrl: URL.createObjectURL(file),
      zoom: 0,
      offsetX: 0,
      offsetY: 0
    });
    setStatus("请在预览工具中调整取景后确认上传");
  }

  function closeImageTool() {
    if (imageTool?.objectUrl) URL.revokeObjectURL(imageTool.objectUrl);
    setImageTool(undefined);
  }

  async function confirmImageTool() {
    if (!imageTool) return;
    const currentTool = imageTool;
    const slot = currentTool.slot;
    setUploadingSlot(slot);
    setStatus("正在生成图片预览");
    try {
      const preparedFile = await createCroppedImageFile(currentTool);
      if (preparedFile.size > maxImageSizeBytes) {
        setStatus(`处理后的图片大小为 ${formatFileSize(preparedFile.size)}，仍超过单张 10MB 限制`);
        return;
      }
      URL.revokeObjectURL(currentTool.objectUrl);
      setImageTool(undefined);
      await uploadAsset(slot, preparedFile);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "图片处理失败");
    } finally {
      setUploadingSlot("");
    }
  }

  async function uploadAsset(slot: MediaUploadSlot, file?: File) {
    if (!file) return;
    setUploadingSlot(slot);
    setStatus("正在上传主页素材");
    try {
      const media = await uploadOrganizerHomeMedia(draft.organizationId, file, slot);
      const pendingMedia = { ...media, reviewStatus: "PENDING" };
      let nextDraft: OrganizerHomeDraft;
      if (slot === "background") {
        nextDraft = {
          ...draft,
          coverFileId: media.id,
          backgroundFileId: media.id,
          coverFile: pendingMedia,
          backgroundFile: pendingMedia,
          status: "PENDING_REVIEW",
          submittedAt: new Date().toISOString()
        };
      } else if (slot === "avatar") {
        nextDraft = {
          ...draft,
          avatarFileId: media.id,
          avatarFile: pendingMedia,
          status: "PENDING_REVIEW",
          submittedAt: new Date().toISOString()
        };
      } else {
        nextDraft = {
          ...draft,
          galleryFileIds: [...(draft.galleryFileIds || []), media.id],
          galleryFiles: [...(draft.galleryFiles || []), media]
        };
      }
      setDraft(nextDraft);
      if (slot === "background" || slot === "avatar") {
        setStatus("图片已上传，正在自动提交主页审核");
        await onSubmit(nextDraft);
      } else {
        setStatus("图集图片已加入草稿，可在底部提交审核");
      }
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "主页素材上传失败");
    } finally {
      setUploadingSlot("");
    }
  }

  function removeGallery(fileId: string) {
    setDraft({
      ...draft,
      galleryFileIds: (draft.galleryFileIds || []).filter((id) => id !== fileId),
      galleryFiles: (draft.galleryFiles || []).filter((file) => file.id !== fileId)
    });
  }

  function moveGallery(fileId: string, direction: -1 | 1) {
    const files = [...(draft.galleryFiles || [])];
    const index = files.findIndex((file) => file.id === fileId);
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= files.length) return;
    const [item] = files.splice(index, 1);
    if (!item) return;
    files.splice(nextIndex, 0, item);
    setDraft({
      ...draft,
      galleryFiles: files,
      galleryFileIds: files.map((file) => file.id)
    });
  }

  const background = draft.backgroundFile || draft.coverFile;
  const avatar = draft.avatarFile;

  return (
    <section className="homeLayout">
      <div className="panel formPanel">
        <div className="panelHead">
          <div>
            <p className="eyebrow">组织主页</p>
            <h2>基础资料与内容草稿</h2>
          </div>
          <span className="tag">{draft.status || "DRAFT"}</span>
        </div>
        <div className="formGrid">
          <label>
            组织名称
            <input value={draft.organizationName || ""} onChange={(event) => setDraft({ ...draft, organizationName: event.target.value })} />
          </label>
          <label>
            一句话介绍
            <input value={draft.slogan || ""} onChange={(event) => setDraft({ ...draft, slogan: event.target.value })} />
          </label>
          <label>
            服务城市
            <input value={(draft.serviceCities || []).join(",")} onChange={(event) => setDraft({ ...draft, serviceCities: splitCsv(event.target.value) })} />
          </label>
          <label>
            模块排序
            <input value={(draft.moduleOrder || []).join(",")} onChange={(event) => setDraft({ ...draft, moduleOrder: splitCsv(event.target.value) })} />
          </label>
          <label className="span2">
            长图文介绍
            <textarea value={draft.longFormIntro || ""} onChange={(event) => setDraft({ ...draft, longFormIntro: event.target.value })} />
          </label>
        </div>
        <div className="visualSection">
          <div className="sectionSubhead">
            <div>
              <p className="eyebrow">主页形象</p>
              <h3>跑团背景与头像</h3>
            </div>
            <span>背景和头像替换后自动提交审核，图集由底部按钮提交</span>
          </div>
          <div className="visualGrid">
            <MediaSlot
              title="跑团背景图"
              description="用于组织主页顶部展示"
              hint={`${imageCropSpecs.background.recommendation}；${imageCropSpecs.background.limit}`}
              media={background}
              draftStatus={draft.status}
              ratio="wide"
              busy={uploadingSlot === "background"}
              onUpload={(file) => openImageTool("background", file)}
              onRemove={() => setDraft({ ...draft, coverFileId: undefined, backgroundFileId: undefined, coverFile: undefined, backgroundFile: undefined })}
            />
            <MediaSlot
              title="跑团头像"
              description="用于组织主页身份识别"
              hint={`${imageCropSpecs.avatar.recommendation}；${imageCropSpecs.avatar.limit}`}
              media={avatar}
              draftStatus={draft.status}
              ratio="avatar"
              busy={uploadingSlot === "avatar"}
              onUpload={(file) => openImageTool("avatar", file)}
              onRemove={() => setDraft({ ...draft, avatarFileId: undefined, avatarFile: undefined })}
            />
          </div>
          <div className="galleryHead">
            <div>
              <strong>主页图集</strong>
              <span>按顺序展示在组织主页中，{imageCropSpecs.gallery.limit}</span>
            </div>
            <UploadButton busy={uploadingSlot === "gallery"} label={uploadingSlot === "gallery" ? "上传中" : "上传图片"} onSelect={(file) => openImageTool("gallery", file)} />
          </div>
          <div className="galleryGrid">
            {(draft.galleryFiles || []).length ? (
              (draft.galleryFiles || []).map((file, index) => (
                <div className="galleryItem" key={file.id}>
                  <MediaPreview media={file} ratio="gallery" />
                  <div className="galleryMeta">
                    <span>{index + 1}</span>
                    <ReviewBadge status={file.reviewStatus} />
                  </div>
                  <div className="miniToolbar">
                    <button type="button" disabled={index === 0} onClick={() => moveGallery(file.id, -1)}>上移</button>
                    <button type="button" disabled={index === (draft.galleryFiles || []).length - 1} onClick={() => moveGallery(file.id, 1)}>下移</button>
                    <button type="button" className="ghostDanger" onClick={() => removeGallery(file.id)}>移除</button>
                  </div>
                </div>
              ))
            ) : (
              <div className="galleryEmpty">暂无图集图片</div>
            )}
          </div>
        </div>
        <div className="toolbar">
          <button disabled={saving || Boolean(uploadingSlot)} className="primary" onClick={() => onSubmit(draft)}>{saving ? "提交中" : "提交图集审核"}</button>
        </div>
        {imageTool ? (
          <ImageToolModal
            tool={imageTool}
            busy={Boolean(uploadingSlot)}
            onCancel={closeImageTool}
            onChange={(nextTool) => setImageTool(nextTool)}
            onConfirm={confirmImageTool}
          />
        ) : null}
      </div>
      <PhonePreview draft={draft} />
      <VersionPanel draft={draft} />
    </section>
  );
}

function MediaSlot({
  title,
  description,
  hint,
  media,
  draftStatus,
  ratio,
  busy,
  onUpload,
  onRemove
}: {
  title: string;
  description: string;
  hint: string;
  media?: OrganizerMediaFile;
  draftStatus?: string;
  ratio: "wide" | "avatar";
  busy: boolean;
  onUpload: (file?: File) => void;
  onRemove: () => void;
}) {
  return (
    <div className="mediaSlot">
      <MediaPreview media={media} ratio={ratio} />
      <div className="mediaSlotBody">
        <div>
          <strong>{title}</strong>
          <span>{description}</span>
          <small>{hint}</small>
        </div>
        <ReviewBadge status={media?.reviewStatus} />
        {draftStatus === "PENDING_REVIEW" && media ? <span className="reviewHint">已提交 CMS，审核中</span> : null}
        <div className="miniToolbar">
          <UploadButton busy={busy} label={media ? "替换" : "上传"} onSelect={onUpload} />
          <button type="button" className="ghostDanger" disabled={!media} onClick={onRemove}>移除</button>
        </div>
      </div>
    </div>
  );
}

function UploadButton({ busy, label, onSelect }: { busy: boolean; label: string; onSelect: (file?: File) => void }) {
  return (
    <label className={busy ? "uploadButton busy" : "uploadButton"}>
      {label}
      <input
        disabled={busy}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={(event) => {
          onSelect(event.target.files?.[0]);
          event.target.value = "";
        }}
      />
    </label>
  );
}

function ImageToolModal({
  tool,
  busy,
  onChange,
  onCancel,
  onConfirm
}: {
  tool: ImageToolState;
  busy: boolean;
  onChange: (tool: ImageToolState) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const spec = imageCropSpecs[tool.slot];
  const zoomScale = imageZoomScale(tool.zoom);
  const previewStyle = {
    transform: `translate(${tool.offsetX / 8}%, ${tool.offsetY / 8}%) scale(${zoomScale})`
  };

  return (
    <div className="imageToolBackdrop" role="presentation">
      <div className="imageToolDialog" role="dialog" aria-modal="true" aria-label={`${spec.title}处理工具`}>
        <div className="imageToolHead">
          <div>
            <p className="eyebrow">IMAGE PREVIEW TOOL</p>
            <h3>{spec.title}截图与预览</h3>
          </div>
          <button type="button" className="iconButton" disabled={busy} onClick={onCancel}>×</button>
        </div>
        <div className="imageToolBody">
          <div className="imageToolStage">
            <div className={`imageToolFrame ${spec.previewClass}`}>
              <img src={tool.objectUrl} alt="" style={previewStyle} />
              <span>{spec.ratioLabel}</span>
            </div>
            <div className="imageToolMeta">
              <strong>{tool.file.name}</strong>
              <span>原图 {formatFileSize(tool.file.size)} · 输出 {spec.width} x {spec.height}</span>
            </div>
          </div>
          <div className="imageToolControls">
            <div className="limitNotice">
              <strong>{spec.recommendation}</strong>
              <span>{spec.limit}</span>
            </div>
            <label>
              <span className="rangeLabel">
                <span>缩放</span>
                <strong>{Math.round(zoomScale * 100)}%</strong>
              </span>
              <input
                type="range"
                min="-100"
                max="100"
                step="1"
                value={tool.zoom}
                onChange={(event) => onChange({ ...tool, zoom: Number(event.target.value) })}
              />
            </label>
            <label>
              水平取景
              <input
                type="range"
                min="-100"
                max="100"
                step="1"
                value={tool.offsetX}
                onChange={(event) => onChange({ ...tool, offsetX: Number(event.target.value) })}
              />
            </label>
            <label>
              垂直取景
              <input
                type="range"
                min="-100"
                max="100"
                step="1"
                value={tool.offsetY}
                onChange={(event) => onChange({ ...tool, offsetY: Number(event.target.value) })}
              />
            </label>
            <div className="imageToolActions">
              <button type="button" className="secondaryButton" disabled={busy} onClick={onCancel}>取消</button>
              <button type="button" className="primary" disabled={busy} onClick={onConfirm}>{busy ? "处理中" : "确认上传"}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MediaPreview({ media, ratio }: { media?: OrganizerMediaFile; ratio: "wide" | "avatar" | "gallery" }) {
  const className = `mediaPreview ${ratio}`;
  return media?.url ? <img className={className} src={media.url} alt="" /> : <div className={`${className} empty`}>待上传</div>;
}

function ReviewBadge({ status }: { status?: string }) {
  const normalized = status || "未上传";
  const className = normalized === "APPROVED" ? "reviewBadge ok" : normalized === "REJECTED" || normalized === "DELETED" ? "reviewBadge bad" : "reviewBadge";
  const labelMap: Record<string, string> = {
    APPROVED: "已通过",
    PENDING: "待审核",
    REJECTED: "已拒绝",
    DELETED: "已删除",
    LOCAL_PREVIEW: "本地预览",
    "未上传": "未上传"
  };
  return <span className={className}>{labelMap[normalized] || normalized}</span>;
}

function datetimeLocalValue(value: unknown) {
  const raw = textValue(value, "");
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  const pad = (amount: number) => String(amount).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function ActivitiesPanel({ rows, organizationId }: { rows: OrganizerRecord[]; organizationId?: string }) {
  const [activities, setActivities] = useState<OrganizerRecord[]>(rows);
  const [selected, setSelected] = useState<OrganizerRecord | undefined>(rows[0]);
  const [form, setForm] = useState<Record<string, string>>({});
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    setActivities(rows);
    setSelected(rows[0]);
    void listOrganizerActivities(organizationId)
      .then((nextRows) => {
        if (!active) return;
        setActivities(nextRows);
        setSelected(nextRows[0]);
      })
      .catch((error) => {
        if (active) setMessage(error instanceof Error ? error.message : "活动列表加载失败");
      });
    return () => {
      active = false;
    };
  }, [rows, organizationId]);

  useEffect(() => {
    setForm({
      title: textValue(selected?.title, ""),
      description: textValue(selected?.description, ""),
      category: textValue(selected?.category, ""),
      locationName: textValue(selected?.locationName, ""),
      locationAddress: textValue(selected?.locationAddress, ""),
      city: textValue(selected?.city, ""),
      startAt: datetimeLocalValue(selected?.startAt),
      registrationDeadline: datetimeLocalValue(selected?.registrationDeadline),
      capacity: textValue(selected?.capacity, ""),
      basePriceFen: textValue(selected?.basePriceFen, ""),
      insuranceFeeFen: textValue(selected?.insuranceFeeFen, "")
    });
    setMessage("");
  }, [selected]);

  async function saveSelectedActivity() {
    const activityId = textValue(selected?.id, "");
    if (!activityId) return;
    setSaving(true);
    setMessage("");
    try {
      const updated = await updateOrganizerActivity(activityId, {
        title: form.title,
        description: form.description,
        category: form.category,
        locationName: form.locationName,
        locationAddress: form.locationAddress,
        city: form.city,
        startAt: form.startAt,
        registrationDeadline: form.registrationDeadline,
        capacity: form.capacity ? Number(form.capacity) : undefined,
        basePriceFen: form.basePriceFen ? Number(form.basePriceFen) : undefined,
        insuranceFeeFen: form.insuranceFeeFen ? Number(form.insuranceFeeFen) : undefined
      });
      setActivities((items) => items.map((item) => (textValue(item.id, "") === activityId ? { ...item, ...updated } : item)));
      setSelected((current) => ({ ...current, ...updated }));
      setMessage("活动信息已保存");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "活动保存失败");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="panel">
      <div className="panelHead">
        <div>
          <p className="eyebrow">活动管理</p>
          <h2>活动列表与现场指标</h2>
        </div>
      </div>
      <DataTable
        columns={["活动", "状态", "报名", "签到", "操作"]}
        rows={activities.map((row) => [
          textValue(row.title || row.id),
          textValue(row.status),
          textValue(row.registrations || row.registrationCount),
          textValue(row.checkIns),
          <button className="smallButton" key={textValue(row.id, "")} onClick={() => setSelected(row)}>编辑</button>
        ])}
      />
      {selected ? (
        <div className="activityEditor">
          <div className="panelHead compact">
            <div>
              <p className="eyebrow">活动信息修改</p>
              <h2>{textValue(selected.title || selected.id)}</h2>
            </div>
          </div>
          <label>
            活动名称
            <input value={form.title || ""} onChange={(event) => setForm({ ...form, title: event.target.value })} />
          </label>
          <label>
            活动说明
            <textarea value={form.description || ""} onChange={(event) => setForm({ ...form, description: event.target.value })} />
          </label>
          <div className="formGrid">
            <label>
              活动类型
              <input value={form.category || ""} onChange={(event) => setForm({ ...form, category: event.target.value })} />
            </label>
            <label>
              城市
              <input value={form.city || ""} onChange={(event) => setForm({ ...form, city: event.target.value })} />
            </label>
          </div>
          <label>
            集合地点
            <input value={form.locationName || ""} onChange={(event) => setForm({ ...form, locationName: event.target.value })} />
          </label>
          <label>
            详细地址
            <input value={form.locationAddress || ""} onChange={(event) => setForm({ ...form, locationAddress: event.target.value })} />
          </label>
          <div className="formGrid">
            <label>
              活动举办时间
              <input type="datetime-local" value={form.startAt || ""} onChange={(event) => setForm({ ...form, startAt: event.target.value })} />
            </label>
            <label>
              报名截止时间
              <input type="datetime-local" value={form.registrationDeadline || ""} onChange={(event) => setForm({ ...form, registrationDeadline: event.target.value })} />
            </label>
          </div>
          <div className="formGrid">
            <label>
              名额
              <input type="number" value={form.capacity || ""} onChange={(event) => setForm({ ...form, capacity: event.target.value })} />
            </label>
            <label>
              报名价 分
              <input type="number" value={form.basePriceFen || ""} onChange={(event) => setForm({ ...form, basePriceFen: event.target.value })} />
            </label>
          </div>
          <label>
            保险费 分
            <input type="number" value={form.insuranceFeeFen || ""} onChange={(event) => setForm({ ...form, insuranceFeeFen: event.target.value })} />
          </label>
          <button disabled={saving || !form.title?.trim()} onClick={() => void saveSelectedActivity()}>{saving ? "保存中" : "保存活动信息"}</button>
          {message ? <p className={message.includes("失败") ? "errorText" : "successText"}>{message}</p> : null}
        </div>
      ) : null}
    </section>
  );
}

function OperationsPanel({ workbench }: { workbench: OrganizerWorkbenchResponse }) {
  const summary = workbench.summary || {};
  return (
    <section className="sectionGrid">
      <div className="panel wide">
        <div className="panelHead">
          <div>
            <p className="eyebrow">经营数据</p>
            <h2>活动经营投影</h2>
          </div>
        </div>
        <div className="metricGrid">
          <Metric label="已支付报名" value={summary.paidRegistrations || 0} />
          <Metric label="团购进展" value="按活动聚合" />
          <Metric label="分销订单" value="只读投影" />
          <Metric label="预计收入" value={formatFen(summary.revenueFen)} />
        </div>
      </div>
      <div className="panel">
        <h2>数据边界</h2>
        <p className="bodyText">PC 后台展示报名、支付、签到和结算摘要；履约隐私、手机号、证件号等敏感信息不在此处展示。</p>
      </div>
    </section>
  );
}

function MembersPanel({ rows, saving, onAdd }: { rows: OrganizerRecord[]; saving: boolean; onAdd: () => void }) {
  return (
    <section className="panel">
      <div className="panelHead">
        <div>
          <p className="eyebrow">协作成员</p>
          <h2>授权管理者</h2>
        </div>
        <button disabled={saving} onClick={onAdd}>新增授权</button>
      </div>
      <DataTable
        columns={["管理者", "活动", "权限状态", "创建时间"]}
        rows={rows.map((row) => [
          textValue(row.managerName || row.managerUserId),
          textValue(row.activityTitle || row.activityId),
          row.active === false ? "已撤销" : "已授权",
          textValue(row.createdAt)
        ])}
      />
    </section>
  );
}

function PhonePreview({ draft }: { draft: OrganizerHomeDraft }) {
  const background = draft.backgroundFile || draft.coverFile;
  const avatar = draft.avatarFile;
  return (
    <div className="phonePanel">
      <div className="phone">
        <div className="phoneCover" style={background?.url ? { backgroundImage: `url(${background.url})` } : undefined} />
        <div className="phoneBody">
          <div className="phoneIdentity">
            {avatar?.url ? <img src={avatar.url} alt="" /> : <span>{(draft.organizationName || "组织").slice(0, 1)}</span>}
            <strong>{draft.organizationName || "组织名称"}</strong>
          </div>
          <span>{draft.slogan || "一句话介绍"}</span>
          <div className="phoneTags">
            {(draft.serviceCities || ["城市"]).map((city) => <span key={city}>{city}</span>)}
          </div>
          <p>{draft.longFormIntro || "组织介绍会在小程序主页中展示。"}</p>
          {(draft.galleryFiles || []).length ? (
            <div className="phoneGallery">
              {(draft.galleryFiles || []).slice(0, 4).map((file) => (file.url ? <img key={file.id} src={file.url} alt="" /> : <span key={file.id} />))}
            </div>
          ) : null}
          <div className="moduleList">
            {(draft.moduleOrder || []).map((module) => <span key={module}>{module}</span>)}
          </div>
        </div>
      </div>
    </div>
  );
}

function VersionPanel({ draft }: { draft: OrganizerHomeDraft }) {
  return (
    <div className="panel versionPanel">
      <div className="panelHead">
        <div>
          <p className="eyebrow">版本记录</p>
          <h2>主页版本</h2>
        </div>
      </div>
      <div className="timeline">
        <span>版本 {draft.version || 0} · {draft.status || "DRAFT"}</span>
        <span>最近同步 {textValue(draft.updatedAt || draft.submittedAt, "本地预览")}</span>
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function DataTable({ columns, rows }: { columns: string[]; rows: ReactNode[][] }) {
  return (
    <table>
      <thead>
        <tr>{columns.map((column) => <th key={column}>{column}</th>)}</tr>
      </thead>
      <tbody>
        {rows.length ? (
          rows.map((row, rowIndex) => (
            <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={`${rowIndex}-${cellIndex}`}>{cell}</td>)}</tr>
          ))
        ) : (
          <tr>
            <td colSpan={columns.length}>暂无数据</td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
